import { PopoverPlacement } from "@idui/react-popover";
import { AddCircleOutlined, Edit } from "@mui/icons-material";
import {
  Autocomplete,
  FormControl,
  FormHelperText,
  IconButton,
  TextField,
} from "@mui/material";
import clsx from "clsx";
import { FC, SyntheticEvent, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import { useDropdownQuickAdd, useDynamicFormsTranslate } from "@ryde/redux";
import {
  checkCleanupNestedValidation,
  dropdownsIsFetchingAtom,
  ICustomField,
  IDropdownItem,
  Log,
  noop,
} from "@ryde/shared";

import { AccessControl, FramerTooltip } from "../../common";
import { FieldExplanationIcon } from "./_parts";
import {
  DropdownBoundFindAreaButton,
  DropdownBoundNoOptions,
  DropdownBoundQuickAddFlowMap,
  DropdownBoundTemplateContainer,
  DropdownBoundTemplateWrapper,
  getDropdownHelpIconOffsetPx,
  ListBox,
  useDropdownTemplate,
} from "./dropdowns";
import { useOpenEntityFormCellTemplate } from "./hooks";

interface IDropdownBoundTemplateProps extends ICustomField {
  onSelect?: (value?: IDropdownItem) => void;
  size?: "small" | "medium";
  onBlur?: (formOnBlur: () => void) => void;
}

export const DropdownBoundTemplate: FC<IDropdownBoundTemplateProps> = ({
  id,
  name,
  formLabel,
  formAttributes,
  formValidation,
  labelTranslated,
  onSelect,
  size = "medium",
  e2eData = "dropdown-bound-template",
  onBlur,
}) => {
  const {
    isAddButtonVisible,
    requiredComputed,
    isDisabled,
    isDisabledHintShown,
    disabledFormLabelTranslated,
    dropdownOptions,
    dropdownItems,
    displayTextReplaced,
    handleInsertItem,
    getOptionLabel,
    fetchItems,
    showFindAreaButton,
    isFindingArea,
    handleFindArea,
  } = useDropdownTemplate("single", name, formAttributes, formValidation);

  const {
    control,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext();
  const { t } = useTranslation();
  const { dropItemsSource, hideDropdownEditButton } = formAttributes || {};

  const { isQuickAddOpen } = useDropdownQuickAdd({
    entity: dropItemsSource,
    name,
  });

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const loadingItems = useRecoilValue(dropdownsIsFetchingAtom);
  const { formLabelTranslated } = useDynamicFormsTranslate({
    name,
    fallbackText: formLabel,
    translatedText: labelTranslated,
  });
  const { openEntityForm } = useOpenEntityFormCellTemplate();
  const selectedValue = watch(name);
  const canShowEditButton =
    !!selectedValue &&
    !!dropItemsSource &&
    dropItemsSource !== "unknown" &&
    !hideDropdownEditButton;

  const onChangeDropdown = (
    e: SyntheticEvent<Element, Event>,
    newValue: string | number,
  ): void => {
    Log.debug("Dropdown bound template value changed to:", newValue);
    setValue(name, newValue, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const handleEditClick = async (): Promise<void> => {
    if (!selectedValue || !dropItemsSource) {
      return;
    }

    const editResult = await openEntityForm({
      entity: dropItemsSource,
      id: selectedValue as number,
    });

    if (!editResult?.success) {
      return;
    }

    const updatedItems = await fetchItems(true);
    const updatedItem = updatedItems?.find(
      item => String(item.value) === String(selectedValue),
    );

    if (!updatedItem) {
      return;
    }

    setValue(name, updatedItem.value, {
      shouldDirty: false,
      shouldValidate: false,
      shouldTouch: false,
    });
    onSelect?.(updatedItem);
  };

  if (displayTextReplaced) {
    return (
      <TextField
        value={displayTextReplaced}
        label={formLabelTranslated}
        disabled
        fullWidth
      />
    );
  }

  return (
    <DropdownBoundTemplateContainer
      className={clsx(
        !isAddButtonVisible && "no-add-button",
        showFindAreaButton && "has-find-area-button",
      )}
    >
      <DropdownBoundTemplateWrapper>
        <Controller
          control={control}
          name={name}
          render={({
            field: { value: formValue, onChange: _onChange, ...restField },
            fieldState: { error },
          }) => {
            return (
              <FormControl
                data-e2e-template="dropdown-bound-template"
                data-e2e-field-name={name}
                required={requiredComputed}
                fullWidth
                error={!!error}
              >
                <Autocomplete
                  {...restField}
                  size={size}
                  data-e2e={e2eData}
                  data-e2e-id={`${name}_${id || ""}`}
                  disabled={isDisabled || loadingItems}
                  options={dropdownOptions || []}
                  value={formValue ?? null}
                  open={isDropdownOpen}
                  onOpen={() => setIsDropdownOpen(true)}
                  onClose={() => setIsDropdownOpen(false)}
                  slotProps={{
                    listbox: {
                      component: ListBox,
                    },
                  }}
                  disableListWrap
                  onChange={(event, newValue) => {
                    checkCleanupNestedValidation(name, errors);
                    onChangeDropdown(event, newValue as string | number);
                    onSelect?.(dropdownItems?.find(e => e.value === newValue));
                  }}
                  onBlur={() => {
                    if (onBlur) {
                      onBlur(restField?.onBlur ?? noop);
                    } else {
                      restField?.onBlur?.();
                    }
                  }}
                  isOptionEqualToValue={(opt, val) =>
                    String(opt) === String(val)
                  }
                  getOptionLabel={getOptionLabel}
                  renderInput={params => (
                    <TextField
                      {...params}
                      error={!!error}
                      label={
                        isDisabledHintShown
                          ? disabledFormLabelTranslated
                          : formLabelTranslated
                      }
                      data-e2e="dropdown-bound-template-input"
                      disabled={isDisabled || loadingItems}
                      required={requiredComputed}
                      autoComplete="off"
                    />
                  )}
                  noOptionsText={
                    <DropdownBoundNoOptions
                      name={name}
                      dropItemsSource={dropItemsSource}
                      closeDropdown={() => setIsDropdownOpen(false)}
                    />
                  }
                />
                {error && (
                  <FormHelperText data-e2e="dropdown-bound-template-error">
                    {error.message}
                  </FormHelperText>
                )}
              </FormControl>
            );
          }}
        />
      </DropdownBoundTemplateWrapper>

      {isAddButtonVisible && (
        <div className="add-button-container">
          <AccessControl entity={dropItemsSource || "unknown"} action="create">
            <FramerTooltip
              content={t("general.ADD_NEW_DROPDOWN_ITEM_TOOLTIP")}
              placement={PopoverPlacement.top}
            >
              <IconButton onClick={handleInsertItem}>
                <AddCircleOutlined />
              </IconButton>
            </FramerTooltip>
          </AccessControl>
        </div>
      )}

      {canShowEditButton && (
        <div className="edit-button-container">
          <AccessControl entity={dropItemsSource} action="update">
            <FramerTooltip
              content={t("general.EDIT")}
              placement={PopoverPlacement.top}
            >
              <IconButton onClick={handleEditClick}>
                <Edit />
              </IconButton>
            </FramerTooltip>
          </AccessControl>
        </div>
      )}

      {showFindAreaButton && (
        <DropdownBoundFindAreaButton
          isFindingArea={isFindingArea}
          handleFindArea={handleFindArea}
        />
      )}

      {isQuickAddOpen && (
        <DropdownBoundQuickAddFlowMap
          name={name}
          formAttributes={formAttributes}
          fetchItems={fetchItems}
        />
      )}

      <FieldExplanationIcon
        formAttributes={formAttributes}
        horizontalOffset={getDropdownHelpIconOffsetPx({
          hasAddButton: isAddButtonVisible,
          hasEditButton: canShowEditButton,
          hasFindAreaButton: showFindAreaButton,
        })}
      />
    </DropdownBoundTemplateContainer>
  );
};
